import React, { useMemo } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import { DBO_V } from '@op/utils/format';
import useIntls from '@opm/hooks/useIntls';
import { TableColumn } from 'tea-component';
import { DEFAULT_METRICS, IMetricItem, resolveThumbnailUrl } from '../../../constants';
import MetricProgress from '@opm/components/MetricProgress';
import VideoInfoCell from '@opm/components/VideoInfoCell';
import './index.scss';
import { DBOM_CONTENT } from '@opm/utils';

interface IVideoTableProps {
  sortBy: any;
  setSortBy: (value: any) => void;
  language: any;
  tableRecords?: Array<any>;
  loading?: boolean;
  total?: number;
  pageInfo: any;
  changePageInfo: (value: any) => void;
  maxMetrics?: Record<string, number>;
  VIDEO_TABLE_METRICS?: IMetricItem[];
}

const tableColumnWidth = 140;

export default function VideoTable({
  sortBy,
  setSortBy,
  language,
  tableRecords = [],
  loading,
  total,
  pageInfo,
  changePageInfo,
  maxMetrics = {},
  VIDEO_TABLE_METRICS = DEFAULT_METRICS,
}: IVideoTableProps) {
  const { intl, isEn } = useIntls();

  const columns: TableColumn<any>[] = useMemo(() => {
    const list: TableColumn<any>[] = [
      {
        key: 'publish_time',
        header: intl('Videos & Posts'),
        width: 510,
        render: (data: any) => (
          <VideoInfoCell
            data={data}
            fieldMapping={{
              cover: resolveThumbnailUrl,
              title: (d: any) => DBOM_CONTENT.videoContent(language, d),
              channelName: () => 'twitter',
              authorName: 'author_name',
              publishTime: 'publish_time',
              videoUrl: 'post_url',
              language: 'lang',
            }}
            desItems={[
              {
                type: 'region',
                field: 'country',
                showText: false,
              },
              { type: 'language', field: 'lang' },
              { type: 'text', field: (item: any) => DBOM_CONTENT.categoryGlobal(isEn, item) },
              { type: 'time', field: 'publish_time' },
            ]}
            durationClassName="dbom-x-video-card-content-duration"
          />
        ),
      },
      ...VIDEO_TABLE_METRICS.map((metric) => ({
        key: metric.key,
        header: intl({ id: metric.label }),
        align: 'right' as const,
        width: tableColumnWidth,
        className: 'dbom-video-metric-cell',
        render: (cvm: any) => {
          const formatFn = metric.format || DBO_V.reviews;
          const value = cvm[metric.key];
          return (
            <div>
              {formatFn(value)}
              <MetricProgress
                biggest={maxMetrics[metric.key] || 0}
                count={value === -1 ? 0 : value}
              />
            </div>
          );
        },
      })),
    ];

    return list;
  }, [intl, language, maxMetrics]);

  return (
    <TableCommonHook
      sortInfo={[sortBy!, setSortBy]}
      records={loading ? [] : tableRecords}
      total={total}
      loading={loading}
      pageInfo={pageInfo}
      setPageInfo={changePageInfo}
      sortColumns={['publish_time', ...VIDEO_TABLE_METRICS.map((m) => m.key)]}
      recordKey={(item) =>
        item.video_title +
        item.publish_time +
        item.author_name +
        item.thumbnail_url
      }
      columns={columns}
      minWidth="auto"
      tableSticky
      pageSizeOptions={[10, 20, 30, 40, 50, 100, 200]}
      verticalTop={false}
    />
  );
}
